import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import Popup from "~/assets/js/components/Popup";
import Headline from "~/assets/js/components/Typography/Headline";
import Button from "~/assets/js/components/Button";
import i18n from "~/assets/js/Helpers/i18n";
import SelectDaySlot from "~/assets/js/pages/Checkout/sections/TimeSlotSelect/SelectDaySlot";
import {
    getDeliveryTimes,
    getHasExpiredSlots,
} from "~/assets/js/Api/deliveryTimesApi";
import moment from "moment";
import useAdditionalUpdateEffect from "~/assets/js/Helpers/hooks/useAdditionalUpdateEffect";
import useAreaCookie from "~/assets/js/Helpers/hooks/useAreaCookie";
import {
    CheckoutContext,
    ACTIONS,
} from "~/assets/js/pages/Checkout/CheckoutProvider";
import FastCheckoutTimeSlot from "./FastCheckoutTimeSlot";
import { getFirstAvailableTimeslot } from "~/assets/js/Helpers/timeslot-utils";
import { ChevronLeft } from "react-feather";
import { useCartContext } from "../../global/cartContext";

const NOTIFICATION = {
    ALL_SLOTS_TAKEN: "timeSlotsTaken",
    NO_SLOTS_TODAY: "noSlotsToday",
    STORE_CLOSED: "storeClosed",
};

const FastCheckoutDaySelect = ({ open, setOpen, setOpenFastCheckout }) => {
    const { cart } = useCartContext();
    const userEmail = cart?.email || "";

    const cookie = useAreaCookie();
    const { state: contextState, dispatch } = useContext(CheckoutContext);

    const selectDayClose = () => {
        setOpen(false);
    };

    const popupConfig = {
        open: open,
        customStyle: "fastcheckout-popup",
        draggable: true,
        handleClose: selectDayClose,
        isClosable: true,
    };

    const [state, setState] = useState({
        isLoading: false,
        groupedDeliveryTimes: {},
        selectedDay: [],
        firstAvailable: {},
        timeSelectOpen: false,
        earlyOnLocationOpen: false,
        timeSelectInfoOpen: false,
        notification: null,
    });

    useEffect(() => {
        const hasExpiredSlots = getHasExpiredSlots();
        const storeClosed = checkStoreClosed();
        const slotNotification = getSlotNotification(hasExpiredSlots);

        if (storeClosed) {
            setState({ ...state, notification: NOTIFICATION.STORE_CLOSED });
            return;
        }

        if (slotNotification) {
            setState({
                ...state,
                notification: slotNotification,
            });
            return;
        }

        setState({ ...state, notification: null });
    }, [state.groupedDeliveryTimes]);

    const getSlotNotification = (hasExpiredSlots) => {
        const today = moment().format("YYYY-MM-DD");
        const firstAvailableDay = Object.keys(state.groupedDeliveryTimes)[0];

        let freeSlotsToday = false;
        let slotsToday = 0;
        if (today === firstAvailableDay) {
            state.groupedDeliveryTimes[firstAvailableDay].map((timeSlot) => {
                slotsToday += 1;
                if (timeSlot.free) {
                    freeSlotsToday = true;
                }
            });
        }
        // if there are no expired slots and no available slots we don't have slot's at all
        if (!slotsToday || !hasExpiredSlots) return NOTIFICATION.NO_SLOTS_TODAY;
        if (!freeSlotsToday) return NOTIFICATION.ALL_SLOTS_TAKEN;
    };

    const checkStoreClosed = () => {
        //temporary solution for store closing at 20:41
        const currentTime = moment();
        const startOfDay = currentTime.clone().startOf("day");
        const timeSinceMidnight = currentTime.diff(startOfDay, "seconds");
        const closingTime = 74460;

        if (timeSinceMidnight > closingTime) {
            return true;
        }
        return false;
    };

    //remove current day if store is closed or no available timeSlots for today
    const removeFirstDay = (groupedDeliveryTimes) => {
        const today = moment().format("YYYY-MM-DD");
        const firstDay = Object.keys(groupedDeliveryTimes)[0];

        if (firstDay === today && state.notification) {
            delete groupedDeliveryTimes[firstDay];
            return groupedDeliveryTimes;
        }
        return groupedDeliveryTimes;
    };

    const fetchDeliveryTimes = async (selectedDeliveryDate) => {
        setState({ ...state, isLoading: true });
        const deliveryTimes = await getDeliveryTimes();
        let currentDay = [];

        if (selectedDeliveryDate) {
            currentDay = deliveryTimes[selectedDeliveryDate];
        }

        setState({
            ...state,
            groupedDeliveryTimes: deliveryTimes,
            selectedDay: currentDay,
            isLoading: false,
        });
    };

    const clearSelection = () => {
        dispatch({
            type: ACTIONS.ADD_TIME_SLOT,
            payload: {},
        });
        setState({ selectedTime: {} });
    };

    useAdditionalUpdateEffect(
        () => fetchDeliveryTimes(contextState?.timeSlot?.deliveryDay),
        cookie,
        clearSelection
    );

    const openPopup = (selectedDay) => {
        setState({
            ...state,
            timeSelectOpen: true,
            selectedDay: selectedDay,
            firstAvailable: getFirstAvailableTimeslot(selectedDay),
        });
    };

    const redirectToFastCheckout = () => {
        setOpen(false);
        setOpenFastCheckout(true);
    };

    return (
        <>
            <Popup {...popupConfig}>
                <div style={{ position: "relative" }}>
                    <div className="header">
                        <ChevronLeft
                            onClick={() => {
                                selectDayClose();
                                setOpenFastCheckout(true);
                            }}
                        />
                        <div className="fastcheckout-popup__title">
                            <Headline size={6} className="headline">
                                {i18n.t(
                                    "cart.fastCheckoutPopup.selectDayTitle"
                                )}
                            </Headline>
                            <div className="email">{userEmail}</div>
                        </div>
                    </div>

                    <div className="fastcheckout-popup__upcontent">
                        <SelectDaySlot
                            groupedDeliveryTimes={removeFirstDay(
                                state.groupedDeliveryTimes
                            )}
                            openTimeSelectPopup={openPopup}
                        />
                    </div>
                    <div className="fastcheckout-popup__downcontent">
                        <Button
                            customStyle={"popup_price"}
                            onClick={redirectToFastCheckout}
                            label={i18n.t(
                                "cart.fastCheckoutPopup.selectDeliveryWindow"
                            )}
                        />
                    </div>
                </div>
            </Popup>
            <FastCheckoutTimeSlot state={state} setState={setState} />
        </>
    );
};

FastCheckoutDaySelect.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    setOpen: PropTypes.func,
    setOpenFastCheckout: PropTypes.func,
};

export default FastCheckoutDaySelect;
